/**
 * Settings → Channels & customers (admin only; App.js wraps it in <AdminRoute>, every endpoint requires admin).
 *
 * The master of every place stock is sold through or sent to: marketplaces, Amazon Vendor, quick commerce
 * (Instamart, Blinkit, Zepto), own websites, B2B customers (Peeko, Ozy) and internal outlets (samples, photoshoot,
 * influencer, staff). List with type / status filters and search (aliases too); "Add channel"; a drawer per channel
 * with Overview (edit; sends the record's version, 409 when someone else saved first), Terms (dated versions:
 * margin, price basis, payment, returns, MOQ; past versions are frozen, future ones editable), Customer details
 * (contact, GSTIN, billing address), Usage and the Audit log. Codes never change (they are stored on sales and
 * orders); a stock bin can be set once. After every save the app's channel lists reload (reloadChannels).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Plus, RefreshCw, Store } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { LoadError, errorMessage } from '../components/LoadError';
import { number } from '../components/sales/format';
import { reloadChannels } from '../hooks/useChannels';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api/channels`;

export const TYPE_LABEL = {
  marketplace: 'Marketplace', marketplace_vendor: 'Marketplace (vendor / PO)', quick_commerce: 'Quick commerce',
  d2c: 'Own website', b2b_customer: 'B2B customer', internal: 'Internal outlet', other: 'Other',
};
export const MODEL_LABEL = {
  marketplace_commission: 'Marketplace commission', outright_po: 'Outright (purchase orders)', outright_b2b: 'Outright sale',
  consignment_sor: 'Consignment / sale or return', dropship: 'Dropship', internal: 'Internal (no sale)',
};
const STATUS_TONE = {
  active: 'bg-green-50 text-green-800 ring-green-200', onboarding: 'bg-sky-50 text-sky-800 ring-sky-200',
  paused: 'bg-amber-50 text-amber-800 ring-amber-200', closed: 'bg-gray-100 text-gray-600 ring-gray-200',
};
const USE_LABEL = { sales: 'Sales', po_dispatch: 'PO dispatch', returns: 'Returns', targets: 'Targets', fee_rules: 'Fee rules',
  identifiers: 'Listing IDs', stock_issue: 'Stock issues', b2b_orders: 'B2B orders' };
const NO_SALE_TYPES = ['b2b_customer', 'internal'];
const PAYMENT_MODE = { NEFT: 'NEFT', RTGS: 'RTGS', UPI: 'UPI', SETTLEMENT: 'Marketplace settlement', CHEQUE: 'Cheque', OTHER: 'Other' };
const PAYMENT_BASIS = { INVOICE_DATE: 'From invoice date', DELIVERY: 'From delivery', SETTLEMENT_CYCLE: 'Settlement cycle' };
const PRICE_BASIS = { MRP_LESS: 'MRP less %', SELLING_PRICE_LESS: 'Selling price less %', TRANSFER_PRICE: 'Transfer price list' };
const RETURN_BEARER = { US: 'We bear it', CHANNEL: 'Channel bears it', SHARED: 'Shared' };
const DAMAGED_POLICY = { CHANNEL_DEBIT: 'Channel debits us', WE_ABSORB: 'We absorb the loss', RETURN_TO_US: 'Returned to us' };
const pct = v => (v == null ? '—' : `${Number(v).toFixed(v % 1 ? 1 : 0)}%`);

const StatusBadge = ({ status }) => (
  <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ring-1 ring-inset ${STATUS_TONE[status] || STATUS_TONE.closed}`}>{status}</span>
);

const Conflicts = ({ rows }) => (rows?.length ? (
  <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" role="alert">
    <p className="flex items-center gap-1.5 font-medium"><AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />Already used</p>
    <ul className="mt-1 space-y-0.5">{rows.map((r, i) => <li key={i}>“{r.spelling}” belongs to {r.channel_name || r.channel}</li>)}</ul>
  </div>
) : null);

const Select = ({ id, label, value, onChange, options, allowEmpty, disabled }) => (
  <div className="space-y-1">
    <Label htmlFor={id} className="text-xs">{label}</Label>
    <select id={id} value={value ?? ''} onChange={e => onChange(e.target.value || null)} disabled={disabled}
      className="h-9 w-full rounded-md border border-gray-300 bg-white px-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:bg-gray-50">
      {allowEmpty && <option value="">—</option>}
      {Object.entries(options).map(([key, text]) => <option key={key} value={key}>{text}</option>)}
    </select>
  </div>
);

const Field = ({ id, label, value, onChange, type = 'text', placeholder, disabled, hint, maxLength }) => (
  <div className="space-y-1">
    <Label htmlFor={id} className="text-xs">{label}</Label>
    <Input id={id} type={type} value={value ?? ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} disabled={disabled} maxLength={maxLength} className="h-9 text-sm" />
    {hint && <p className="text-[11px] text-gray-500">{hint}</p>}
  </div>
);

const num = v => (v === '' || v == null ? null : Number(v));

/** The channel fields form (create and Overview edit). */
const parseAliases = text => text.split(',').map(a => a.trim()).filter(Boolean);
// Form-only fields never go to the API
const toPayload = ({ codeTouched, aliasText, ...body }) => ({ ...body, aliases: aliasText === undefined ? body.aliases : parseAliases(aliasText) });

const ChannelForm = ({ value, onChange, options, creating, lockedBin }) => {
  const set = patch => onChange({ ...value, ...patch });
  const typeDefaults = options?.type_defaults?.[value.type] || {};
  const onType = type => {
    const d = options?.type_defaults?.[type] || {};
    set({ type, business_model: d.business_model, uses: d.uses, report: d.report,
      issue: type === 'internal' ? (value.issue || { returnable: true, default_return_days: 30, price_received_allowed: false }) : null });
  };
  const toggleUse = use => set({ uses: (value.uses || []).includes(use) ? value.uses.filter(u => u !== use) : [...(value.uses || []), use] });
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field id="ch-name" label="Name" value={value.name} onChange={name => set({ name, ...(creating && !value.codeTouched ? { code: name.toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 32) } : {}) })} maxLength={80} />
        <Field id="ch-code" label="Code" value={value.code} onChange={code => set({ code: code.toUpperCase(), codeTouched: true })} disabled={!creating}
          hint={creating ? 'Stored on every sale and order; cannot be changed later.' : 'Codes never change.'} maxLength={32} />
        <Field id="ch-short" label="Short name" value={value.short_name} onChange={short_name => set({ short_name })} maxLength={40} />
        <Select id="ch-type" label="Type" value={value.type} onChange={onType} options={TYPE_LABEL} />
        <Select id="ch-model" label="Business model" value={value.business_model} onChange={business_model => set({ business_model })} options={MODEL_LABEL} />
        <Select id="ch-status" label="Status" value={value.status} onChange={status => set({ status })} options={{ active: 'Active', onboarding: 'Onboarding', paused: 'Paused', closed: 'Closed' }} />
        {value.status === 'closed' && <Field id="ch-closed" label="Closed on" type="date" value={value.closed_on} onChange={closed_on => set({ closed_on })} />}
        <Field id="ch-bin" label="Stock bin" value={value.bin_key} onChange={bin_key => set({ bin_key: bin_key.toLowerCase() })} disabled={lockedBin}
          placeholder="e.g. instamart" hint={lockedBin ? 'Stock is kept under this bin; it cannot be changed.' : 'Optional. Set once: stock sent to this channel is kept under it.'} maxLength={31} />
        <div className="space-y-1">
          <Label htmlFor="ch-color" className="text-xs">Colour</Label>
          <input id="ch-color" type="color" value={value.color || '#64748B'} onChange={e => set({ color: e.target.value })} className="h-9 w-16 cursor-pointer rounded border border-gray-300" />
        </div>
      </div>
      <Field id="ch-aliases" label="Other spellings (comma separated)" value={value.aliasText ?? (value.aliases || []).join(', ')}
        onChange={text => set({ aliasText: text, aliases: parseAliases(text) })}
        hint="Uploads and APIs that write any of these land on this channel (case, spaces and hyphens ignored)." />
      <fieldset className="space-y-2">
        <legend className="text-xs font-medium text-gray-700">Used for</legend>
        <div className="flex flex-wrap gap-2">
          {(options?.uses || Object.keys(USE_LABEL)).map(use => {
            const disabled = value.type === 'internal' ? use !== 'stock_issue'
              : use === 'stock_issue' || (value.type === 'b2b_customer' && (use === 'sales' || use === 'targets'));
            return (
              <label key={use} className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs ${disabled ? 'opacity-50' : ''}`}>
                <input type="checkbox" checked={(value.uses || []).includes(use)} onChange={() => toggleUse(use)} disabled={disabled} />{USE_LABEL[use] || use}
              </label>
            );
          })}
        </div>
      </fieldset>
      <fieldset className="space-y-2">
        <legend className="text-xs font-medium text-gray-700">In reports</legend>
        {NO_SALE_TYPES.includes(value.type) ? (
          <p className="text-[12px] text-gray-600" data-testid="channel-out-of-revenue">
            {value.type === 'b2b_customer' ? 'B2B customers stay out of overall revenue. Their orders are kept apart, with PO (B2B) revenue.' : 'Internal outlets are never revenue. Stock sent there is a stock issue.'}
          </p>
        ) : (
          <>
            <label className="flex items-center justify-between gap-3 text-sm">
              <span>Counts as revenue</span>
              <Switch checked={(value.report || typeDefaults.report || {}).revenue !== false} onCheckedChange={checked => set({ report: { ...(value.report || typeDefaults.report), revenue: checked } })} aria-label="Counts as revenue" />
            </label>
            <p className="text-[11px] text-gray-500">Off means sales cannot be recorded for this channel. A channel that already has sales cannot be turned off.</p>
          </>
        )}
      </fieldset>
      {value.type === 'internal' && (
        <fieldset className="grid gap-3 rounded-lg border border-gray-200 p-3 sm:grid-cols-3">
          <legend className="px-1 text-xs font-medium text-gray-700">Stock issues</legend>
          <label className="flex items-center gap-2 text-sm"><Switch checked={Boolean(value.issue?.returnable)} onCheckedChange={returnable => set({ issue: { ...value.issue, returnable } })} aria-label="Usually comes back" />Usually comes back</label>
          {value.issue?.returnable && <Field id="ch-days" label="Return within (days)" type="number" value={value.issue?.default_return_days} onChange={d => set({ issue: { ...value.issue, default_return_days: num(d) } })} />}
          <label className="flex items-center gap-2 text-sm"><Switch checked={Boolean(value.issue?.price_received_allowed)} onCheckedChange={allowed => set({ issue: { ...value.issue, price_received_allowed: allowed } })} aria-label="A price is paid" />A price is paid (staff)</label>
        </fieldset>
      )}
      <div className="space-y-1">
        <Label htmlFor="ch-notes" className="text-xs">Notes</Label>
        <Textarea id="ch-notes" value={value.notes || ''} onChange={e => set({ notes: e.target.value })} rows={2} maxLength={2000} />
      </div>
    </div>
  );
};

const CustomerForm = ({ value, onChange }) => {
  const contact = value.contact || {};
  const billing = value.billing || {};
  const setC = patch => onChange({ ...value, contact: { ...contact, ...patch } });
  const setB = patch => onChange({ ...value, billing: { ...billing, ...patch } });
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <Field id="c-person" label="Contact person" value={contact.person} onChange={person => setC({ person })} />
        <Field id="c-phone" label="Phone" value={contact.phone} onChange={phone => setC({ phone })} />
        <Field id="c-email" label="Email" type="email" value={contact.email} onChange={email => setC({ email })} />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field id="b-legal" label="Legal name" value={billing.legal_name} onChange={legal_name => setB({ legal_name })} />
        <Field id="b-gstin" label="GSTIN" value={billing.gstin} onChange={gstin => setB({ gstin: gstin.toUpperCase(), state_code: undefined })} maxLength={15} hint="Checked for format; state code is taken from it." />
        <Field id="b-pan" label="PAN" value={billing.pan} onChange={pan => setB({ pan: pan.toUpperCase() })} maxLength={10} />
        <Field id="b-state" label="State" value={billing.state} onChange={state => setB({ state })} />
        <Field id="b-pin" label="PIN code" value={billing.pincode} onChange={pincode => setB({ pincode })} maxLength={6} />
      </div>
      <div className="space-y-1">
        <Label htmlFor="b-addr" className="text-xs">Billing address</Label>
        <Textarea id="b-addr" value={billing.address || ''} onChange={e => setB({ address: e.target.value })} rows={3} maxLength={400} />
      </div>
      <p className="text-[11px] text-gray-500">Reference details only: invoices are raised in Tally.</p>
    </div>
  );
};

const EMPTY_TERMS = { effective_from: '', margin_pct: '', commission_pct: '', price_basis: { kind: '', pct: '' },
  payment: { credit_days: '', mode: '', basis: '' }, returns: { window_days: '', borne_by: '', damaged_policy: '', restock: true, max_return_pct: '' },
  moq_units: '', moq_value: '', notes: '' };

const termsBody = t => ({
  effective_from: t.effective_from, margin_pct: num(t.margin_pct), commission_pct: num(t.commission_pct),
  price_basis: { kind: t.price_basis?.kind || null, pct: num(t.price_basis?.pct) },
  payment: { credit_days: num(t.payment?.credit_days), mode: t.payment?.mode || null, basis: t.payment?.basis || null },
  returns: { window_days: num(t.returns?.window_days), borne_by: t.returns?.borne_by || null, damaged_policy: t.returns?.damaged_policy || null,
    restock: t.returns?.restock !== false, max_return_pct: num(t.returns?.max_return_pct) },
  moq_units: num(t.moq_units), moq_value: num(t.moq_value), notes: t.notes || null,
});

const TermsForm = ({ value, onChange }) => {
  const set = patch => onChange({ ...value, ...patch });
  return (
    <div className="grid gap-3 sm:grid-cols-3">
      <Field id="t-from" label="Applies from" type="date" value={value.effective_from} onChange={effective_from => set({ effective_from })} />
      <Field id="t-margin" label="Our margin / discount %" type="number" value={value.margin_pct} onChange={margin_pct => set({ margin_pct })} />
      <Field id="t-comm" label="Commission %" type="number" value={value.commission_pct} onChange={commission_pct => set({ commission_pct })} />
      <Select id="t-pb" label="Price basis" value={value.price_basis?.kind} onChange={kind => set({ price_basis: { ...value.price_basis, kind } })} options={PRICE_BASIS} allowEmpty />
      <Field id="t-pbp" label="Price basis %" type="number" value={value.price_basis?.pct} onChange={p => set({ price_basis: { ...value.price_basis, pct: p } })} />
      <Field id="t-cd" label="Credit days" type="number" value={value.payment?.credit_days} onChange={credit_days => set({ payment: { ...value.payment, credit_days } })} />
      <Select id="t-pm" label="Payment mode" value={value.payment?.mode} onChange={mode => set({ payment: { ...value.payment, mode } })} options={PAYMENT_MODE} allowEmpty />
      <Select id="t-pbs" label="Credit counted" value={value.payment?.basis} onChange={basis => set({ payment: { ...value.payment, basis } })} options={PAYMENT_BASIS} allowEmpty />
      <Field id="t-rw" label="Return window (days)" type="number" value={value.returns?.window_days} onChange={window_days => set({ returns: { ...value.returns, window_days } })} />
      <Select id="t-rb" label="Returns borne by" value={value.returns?.borne_by} onChange={borne_by => set({ returns: { ...value.returns, borne_by } })} options={RETURN_BEARER} allowEmpty />
      <Select id="t-rd" label="Damaged returns" value={value.returns?.damaged_policy} onChange={damaged_policy => set({ returns: { ...value.returns, damaged_policy } })} options={DAMAGED_POLICY} allowEmpty />
      <Field id="t-rmax" label="Max return %" type="number" value={value.returns?.max_return_pct} onChange={max_return_pct => set({ returns: { ...value.returns, max_return_pct } })} />
      <Field id="t-moq" label="MOQ (units)" type="number" value={value.moq_units} onChange={moq_units => set({ moq_units })} />
      <Field id="t-moqv" label="MOQ (₹)" type="number" value={value.moq_value} onChange={moq_value => set({ moq_value })} />
      <label className="flex items-center gap-2 self-end text-sm"><Switch checked={value.returns?.restock !== false} onCheckedChange={restock => set({ returns: { ...value.returns, restock } })} aria-label="Returns go back to stock" />Returns go back to stock</label>
      <div className="space-y-1 sm:col-span-3">
        <Label htmlFor="t-notes" className="text-xs">Notes</Label>
        <Textarea id="t-notes" value={value.notes || ''} onChange={e => set({ notes: e.target.value })} rows={2} maxLength={1000} />
      </div>
    </div>
  );
};

const termsSummary = t => [
  t.margin_pct != null && `margin ${pct(t.margin_pct)}`, t.commission_pct != null && `commission ${pct(t.commission_pct)}`,
  t.price_basis?.kind && `${PRICE_BASIS[t.price_basis.kind]}${t.price_basis.pct != null ? ` ${pct(t.price_basis.pct)}` : ''}`,
  t.payment?.credit_days != null && `${t.payment.credit_days} days credit${t.payment.mode ? ` · ${PAYMENT_MODE[t.payment.mode]}` : ''}`,
  t.returns?.window_days != null && `returns ${t.returns.window_days} days${t.returns.borne_by ? ` · ${RETURN_BEARER[t.returns.borne_by]}` : ''}`,
  t.moq_units != null && `MOQ ${number(t.moq_units)} units`,
].filter(Boolean).join(' · ') || 'No terms filled';

const TermsTab = ({ channel, onSaved }) => {
  const today = new Date(Date.now() + 5.5 * 3600e3).toISOString().slice(0, 10);   // India date
  const [draft, setDraft] = useState(null);       // { mode: 'add' | version_id, value }
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      if (draft.mode === 'add') await axios.post(`${API}/${channel.code}/terms`, termsBody(draft.value));
      else await axios.put(`${API}/${channel.code}/terms/${draft.mode}`, termsBody(draft.value));
      toast.success('Terms saved');
      setDraft(null);
      onSaved();
    } catch (error) {
      toast.error(errorMessage(error, 'Could not save the terms'));
    } finally {
      setBusy(false);
    }
  };
  const versions = [...(channel.terms || [])].sort((a, b) => b.effective_from.localeCompare(a.effective_from));
  const inForce = channel.terms_today?.version_id;
  return (
    <div className="space-y-3" data-testid="channel-terms">
      {!draft && <Button size="sm" onClick={() => setDraft({ mode: 'add', value: { ...EMPTY_TERMS, effective_from: today } })} data-testid="channel-terms-add"><Plus className="mr-1 h-4 w-4" aria-hidden="true" />New terms version</Button>}
      {draft && (
        <div className="space-y-3 rounded-lg border border-blue-200 bg-blue-50/40 p-3">
          <p className="text-sm font-medium">{draft.mode === 'add' ? 'New terms version' : 'Edit future terms'}</p>
          <TermsForm value={draft.value} onChange={value => setDraft({ ...draft, value })} />
          <div className="flex gap-2">
            <Button size="sm" onClick={save} disabled={busy || !draft.value.effective_from}>{busy ? 'Saving…' : 'Save terms'}</Button>
            <Button size="sm" variant="outline" onClick={() => setDraft(null)}>Cancel</Button>
          </div>
          <p className="text-[11px] text-gray-500">Terms already in force cannot be edited: add a new version from the date the change applies.</p>
        </div>
      )}
      {!versions.length && !draft && <p className="text-sm text-gray-500">No terms yet.</p>}
      <ol className="space-y-2">
        {versions.map(t => (
          <li key={t.version_id} className={`rounded-lg border p-3 text-sm ${t.version_id === inForce ? 'border-green-300 bg-green-50/50' : 'border-gray-200'}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-medium tabular-nums">From {t.effective_from}{t.version_id === inForce && <span className="ml-2 rounded-full bg-green-100 px-2 text-[11px] text-green-800">In force today</span>}{t.effective_from > today && <span className="ml-2 rounded-full bg-sky-100 px-2 text-[11px] text-sky-800">Upcoming</span>}</span>
              {t.effective_from > today && !draft && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setDraft({ mode: t.version_id, value: { ...EMPTY_TERMS, ...t } })}>Edit</Button>}
            </div>
            <p className="mt-1 text-xs text-gray-700">{termsSummary(t)}</p>
            {t.returns?.damaged_policy && <p className="text-xs text-gray-600">Damaged returns: {DAMAGED_POLICY[t.returns.damaged_policy]}{t.returns.restock === false ? ' · returns not restocked' : ''}</p>}
            {t.notes && <p className="mt-1 text-xs text-gray-500">{t.notes}</p>}
            <p className="mt-1 text-[11px] text-gray-400">Added by {t.created_by || '—'}{t.edited_by ? ` · edited by ${t.edited_by}` : ''}</p>
          </li>
        ))}
      </ol>
    </div>
  );
};

const AuditTab = ({ events }) => (
  <ol className="space-y-2" data-testid="channel-audit">
    {!events?.length && <li className="text-sm text-gray-500">No changes recorded yet.</li>}
    {events?.map(e => (
      <li key={`${e.at}-${e.action}`} className="rounded-lg border border-gray-200 p-2 text-xs">
        <div className="flex flex-wrap justify-between gap-2"><span className="font-medium capitalize">{e.action.replace('_', ' ')}</span><span className="text-gray-500">{new Date(e.at).toLocaleString('en-IN')} · {e.by?.email}</span></div>
        {e.note && <p className="mt-0.5 text-gray-700">“{e.note}”</p>}
        <ul className="mt-1 space-y-0.5 text-gray-600">
          {Object.entries(e.changes || {}).filter(([key]) => key !== 'terms').slice(0, 12).map(([key, [before, after]]) => (
            <li key={key}><span className="font-mono">{key}</span>: {JSON.stringify(before) ?? '—'} → {JSON.stringify(after) ?? '—'}</li>
          ))}
          {e.changes?.terms && <li>Terms from {e.changes.terms[1]?.effective_from || e.changes.terms[0]?.effective_from}</li>}
        </ul>
      </li>
    ))}
  </ol>
);

const UsageTab = ({ usage }) => {
  const rows = [['sales', 'Sales rows'], ['retail_pos', 'Purchase orders'], ['stock_issues', 'Stock issues'], ['returns', 'Returns'], ['targets', 'Targets'],
    ['fee_rules', 'Fee rule sets'], ['integrations', 'API connections'], ['cutovers', 'API cut-overs'], ['stock', 'SKUs with stock in its bin']];
  return (
    <dl className="grid grid-cols-2 gap-2 text-sm" data-testid="channel-usage">
      {rows.map(([key, text]) => <div key={key} className="rounded-lg border border-gray-200 p-2"><dt className="text-xs text-gray-500">{text}</dt><dd className="font-semibold tabular-nums">{key in (usage || {}) ? number(usage[key]) : '—'}</dd></div>)}
    </dl>
  );
};

const editable = doc => ({ code: doc.code, name: doc.name, short_name: doc.short_name, type: doc.type, business_model: doc.business_model, status: doc.status,
  closed_on: doc.closed_on, group: doc.group, bin_key: doc.bin_key, uses: doc.uses, report: doc.report, issue: doc.issue,
  aliases: doc.aliases, color: doc.color, notes: doc.notes, contact: doc.contact, billing: doc.billing });

const ChannelDrawer = ({ code, options, onClose, onChanged }) => {
  const [doc, setDoc] = useState(null);
  const [form, setForm] = useState(null);
  // The record version the form was loaded from: a save sends it, so another admin's change in between gives a 409
  const [formVersion, setFormVersion] = useState(null);
  const [error, setError] = useState(null);
  const [conflicts, setConflicts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const load = useCallback(async ({ keepForm = false } = {}) => {
    try {
      const { data } = await axios.get(`${API}/${code}`);
      setDoc(data); setError(null);
      if (keepForm !== true) { setForm(editable(data)); setFormVersion(data.version); }
      return data;
    } catch (err) {
      setError(err);
      return null;
    }
  }, [code]);
  useEffect(() => {
    // A different channel starts clean: no note, conflicts or error carried over from the last one
    setNote(''); setConflicts([]); setError(null);
    if (code) { setDoc(null); load(); }
  }, [code, load]);
  const save = async () => {
    setBusy(true); setConflicts([]);
    try {
      const payload = { ...toPayload(form), version: formVersion ?? doc.version, note: note || undefined };
      if (doc.bin_key) delete payload.bin_key;
      await axios.patch(`${API}/${code}`, payload);
      toast.success('Saved');
      setNote('');
      await load(); await reloadChannels(); onChanged();
    } catch (err) {
      setConflicts(err.response?.data?.detail?.conflicts || []);
      toast.error(errorMessage(err, 'Could not save'));
    } finally {
      setBusy(false);
    }
  };
  // A terms save bumps the record version by one. Unsaved Overview / Customer edits are kept and follow that bump
  // only when nobody else saved in between; otherwise the old version stays, so the next save gets a 409.
  const afterTerms = async () => {
    const before = doc.version;
    const data = await load({ keepForm: true });
    if (data && formVersion === before && data.version === before + 1) setFormVersion(data.version);
    onChanged();
  };
  const remove = async () => {
    if (!window.confirm(`Delete ${doc.name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/${code}`);
      toast.success(`${doc.name} deleted`);
      await reloadChannels(); onChanged(); onClose();
    } catch (err) {
      toast.error(errorMessage(err, 'Could not delete'));
    }
  };
  return (
    <Sheet open={Boolean(code)} onOpenChange={open => { if (!open) onClose(); }}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-2xl" data-testid="channel-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">{doc && <span className="h-3 w-3 rounded-full" style={{ background: doc.color }} aria-hidden="true" />}{doc?.name || code}</SheetTitle>
          <SheetDescription>{doc ? `${code} · ${TYPE_LABEL[doc.type]} · ${MODEL_LABEL[doc.business_model]}` : 'Loading…'}</SheetDescription>
        </SheetHeader>
        {error && <div className="mt-4"><LoadError what="this channel" error={error} onRetry={load} /></div>}
        {doc && form && (
          <Tabs defaultValue="overview" className="mt-4">
            <TabsList className="flex-wrap">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="terms">Terms</TabsTrigger>
              <TabsTrigger value="customer">Customer details</TabsTrigger>
              <TabsTrigger value="usage">Usage</TabsTrigger>
              <TabsTrigger value="audit">Audit log</TabsTrigger>
            </TabsList>
            <TabsContent value="overview" className="mt-4 space-y-4">
              <ChannelForm value={form} onChange={setForm} options={options} lockedBin={Boolean(doc.bin_key)} />
              <Field id="ch-note" label="Reason for the change (audit log)" value={note} onChange={setNote} maxLength={500} />
              <Conflicts rows={conflicts} />
              <div className="flex flex-wrap gap-2">
                <Button onClick={save} disabled={busy} data-testid="channel-save">{busy ? 'Saving…' : 'Save'}</Button>
                {!doc.system && !doc.usage?.in_use && <Button variant="outline" className="text-red-700" onClick={remove}>Delete</Button>}
              </div>
              {doc.system && <p className="text-[11px] text-gray-500">Built-in channel: it can be renamed, paused or closed, not deleted.</p>}
            </TabsContent>
            <TabsContent value="terms" className="mt-4"><TermsTab channel={doc} onSaved={afterTerms} /></TabsContent>
            <TabsContent value="customer" className="mt-4 space-y-3">
              <CustomerForm value={form} onChange={setForm} />
              <Conflicts rows={conflicts} />
              <Button onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save details'}</Button>
            </TabsContent>
            <TabsContent value="usage" className="mt-4"><UsageTab usage={doc.usage} /></TabsContent>
            <TabsContent value="audit" className="mt-4"><AuditTab events={doc.events} /></TabsContent>
          </Tabs>
        )}
      </SheetContent>
    </Sheet>
  );
};

const NEW_CHANNEL = { code: '', name: '', type: 'b2b_customer', business_model: 'outright_b2b', status: 'onboarding', uses: ['b2b_orders'],
  report: { revenue: false, demand: true, show_in_sales_reports: false }, aliases: [], color: '#64748B' };

const AddDialog = ({ open, onOpenChange, options, onCreated }) => {
  const [form, setForm] = useState(NEW_CHANNEL);
  const [busy, setBusy] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  useEffect(() => { if (open) { setForm(NEW_CHANNEL); setConflicts([]); } }, [open]);
  const create = async () => {
    setBusy(true); setConflicts([]);
    try {
      const body = toPayload(form);
      const { data } = await axios.post(API, { ...body, bin_key: body.bin_key || undefined });
      toast.success(`${data.name} added`);
      await reloadChannels();
      onCreated(data.code);
    } catch (err) {
      setConflicts(err.response?.data?.detail?.conflicts || []);
      toast.error(errorMessage(err, 'Could not add the channel'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="channel-add-dialog">
        <DialogHeader>
          <DialogTitle>Add a channel or customer</DialogTitle>
          <DialogDescription>Marketplace, quick commerce, B2B customer (e.g. Peeko) or an internal outlet. Terms and customer details are added after it is created.</DialogDescription>
        </DialogHeader>
        <ChannelForm value={form} onChange={setForm} options={options} creating />
        <Conflicts rows={conflicts} />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={create} disabled={busy || !form.name || !form.code} data-testid="channel-create">{busy ? 'Adding…' : 'Add'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function ChannelsPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [openCode, setOpenCode] = useState(null);
  const [adding, setAdding] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: body } = await axios.get(`${API}/admin`);
      setData(body); setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  const rows = useMemo(() => {
    const term = query.trim().toLowerCase();
    return (data?.channels || []).filter(c => (!type || c.type === type) && (!status || c.status === status)
      && (!term || [c.code, c.name, c.short_name, ...(c.aliases || []), c.billing?.gstin].filter(Boolean).join(' ').toLowerCase().includes(term)));
  }, [data, query, type, status]);
  const counts = useMemo(() => (data?.channels || []).reduce((acc, c) => ({ ...acc, [c.type]: (acc[c.type] || 0) + 1 }), {}), [data]);
  return (
    <div className="space-y-5" data-testid="channels-page">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl"><Store className="h-7 w-7" aria-hidden="true" />Channels & customers</h1>
          <p className="mt-1 text-sm text-gray-500">Every place stock is sold through or sent to, with its business model and dated terms. Uploads and APIs recognise each channel by its code or any of its spellings.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} aria-label="Refresh"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" /></Button>
          <Button size="sm" onClick={() => setAdding(true)} data-testid="channel-add"><Plus className="mr-1 h-4 w-4" aria-hidden="true" />Add channel</Button>
        </div>
      </header>
      <div className="flex flex-wrap items-center gap-2">
        <Input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search name, code, spelling or GSTIN…" aria-label="Search channels" className="h-9 w-full sm:max-w-xs" />
        <select value={type} onChange={e => setType(e.target.value)} aria-label="Filter by type" className="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm">
          <option value="">All types</option>
          {Object.entries(TYPE_LABEL).map(([key, text]) => <option key={key} value={key}>{text}{counts[key] ? ` (${counts[key]})` : ''}</option>)}
        </select>
        <select value={status} onChange={e => setStatus(e.target.value)} aria-label="Filter by status" className="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm">
          <option value="">Any status</option>
          {['active', 'onboarding', 'paused', 'closed'].map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
        </select>
        <span className="ml-auto text-xs text-gray-500">{rows.length} of {data?.channels?.length ?? 0}</span>
      </div>
      {error && <LoadError what="channels" error={error} onRetry={load} retrying={loading} />}
      {data && (
        <div className="relative overflow-x-auto rounded-xl border border-gray-200 bg-white">
          <table className="w-full min-w-[820px] text-left text-sm" data-testid="channels-table">
            <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500">
              <tr><th className="px-3 py-2">Channel</th><th className="px-3 py-2">Type</th><th className="px-3 py-2">Business model</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Stock bin</th><th className="px-3 py-2">Terms today</th><th className="px-3 py-2">In use</th></tr>
            </thead>
            <tbody>
              {rows.map(c => (
                <tr key={c.code} className="cursor-pointer border-t border-gray-100 hover:bg-gray-50" onClick={() => setOpenCode(c.code)} data-testid={`channel-row-${c.code}`}>
                  <td className="px-3 py-2">
                    <button type="button" className="flex items-center gap-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" onClick={e => { e.stopPropagation(); setOpenCode(c.code); }}>
                      <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: c.color }} aria-hidden="true" />
                      <span><span className="font-medium text-gray-900">{c.name}</span><span className="block font-mono text-[11px] text-gray-500">{c.code}{c.aliases?.length ? ` · ${c.aliases.join(', ')}` : ''}</span></span>
                    </button>
                  </td>
                  <td className="px-3 py-2 text-gray-700">{TYPE_LABEL[c.type]}</td>
                  <td className="px-3 py-2 text-gray-700">{MODEL_LABEL[c.business_model]}</td>
                  <td className="px-3 py-2"><StatusBadge status={c.status} /></td>
                  <td className="px-3 py-2 font-mono text-xs text-gray-600">{c.bin_key || '—'}</td>
                  <td className="px-3 py-2 text-xs text-gray-600">{c.terms_today ? termsSummary(c.terms_today) : '—'}</td>
                  <td className="px-3 py-2 text-xs text-gray-600">{c.usage?.in_use ? 'Yes' : 'Not yet'}</td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={7} className="px-3 py-8 text-center text-gray-500">No channel matches.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <AddDialog open={adding} onOpenChange={setAdding} options={data?.options} onCreated={code => { setAdding(false); load(); setOpenCode(code); }} />
      <ChannelDrawer code={openCode} options={data?.options} onClose={() => setOpenCode(null)} onChanged={load} />
    </div>
  );
}
